import type { InstanceEnvironment } from './types';

/**
 * Die Umgebungen einer Instanz, an einer Stelle.
 *
 * Vorher standen Beschriftung und Farbe in drei Dateien nebeneinander -- und
 * genau so laeuft es auseinander: eine neue Umgebung kommt in der Tabelle an,
 * im Anlegeformular aber nicht.
 *
 * `kurz` ist der Teil, der in Subdomain und Stack-Namen landet. Er ist
 * absichtlich englisch und knapp: er steht in `docker ps`, im Proxy und in der
 * Adresszeile, und dort will niemand „abnahme" lesen.
 */
export interface UmgebungsDefinition {
  wert: InstanceEnvironment;
  kurz: string;
  /** Schluessel in den Sprachdateien, unterhalb von `environment.`. */
  schluessel: string;
  farbe: string;
}

export const UMGEBUNGEN: UmgebungsDefinition[] = [
  {
    wert: 'PRODUCTION',
    kurz: 'prod',
    schluessel: 'production',
    farbe: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  },
  {
    wert: 'STAGING',
    kurz: 'stage',
    schluessel: 'staging',
    farbe: 'bg-amber-50 text-amber-700 ring-amber-200',
  },
  {
    wert: 'TEST',
    kurz: 'test',
    schluessel: 'test',
    farbe: 'bg-sky-50 text-sky-700 ring-sky-200',
  },
  {
    wert: 'DEMO',
    kurz: 'demo',
    schluessel: 'demo',
    farbe: 'bg-violet-50 text-violet-700 ring-violet-200',
  },
  {
    wert: 'DEV',
    kurz: 'dev',
    schluessel: 'dev',
    farbe: 'bg-slate-100 text-slate-600 ring-slate-300',
  },
];

export function umgebung(wert: InstanceEnvironment | null): UmgebungsDefinition | undefined {
  return UMGEBUNGEN.find((u) => u.wert === wert);
}


/**
 * Zustände, die ein Mensch setzen darf.
 *
 * Bewusst nicht alle. `DEPLOYING`, `UPGRADING`, `DELETING` und `FAILED` setzen
 * die Hintergrundaufträge — sie von Hand zu wählen hiesse, dem System zu
 * erzählen, es arbeite gerade, während nichts läuft. Wer `DEPLOYING` einträgt,
 * wartet danach auf etwas, das nie kommt.
 *
 * Was hier steht, sind Entscheidungen: läuft, pausiert, abgelegt, beendet.
 */
export const SETZBARE_ZUSTAENDE = [
  { wert: 'ACTIVE', schluessel: 'active' },
  { wert: 'SUSPENDED', schluessel: 'suspended' },
  { wert: 'ARCHIVED', schluessel: 'archived' },
  { wert: 'TERMINATED', schluessel: 'terminated' },
] as const;

export type SetzbarerZustand = (typeof SETZBARE_ZUSTAENDE)[number]['wert'];

export function istSetzbar(status: string): status is SetzbarerZustand {
  return SETZBARE_ZUSTAENDE.some((z) => z.wert === status);
}


/**
 * Die öffentliche Adresse einer Instanz.
 *
 * `null`, wenn keine Domain hinterlegt ist — ein Link, der auf `https://`
 * zeigt, ist schlimmer als kein Link.
 *
 * Die Subdomain ist optional, und das ist kein Sonderfall, sondern der halbe
 * Bestand: bei `junix` steht sie getrennt (`junix` + `editionen-halbig.de`),
 * bei `bryve-demo` trägt die Domain schon den vollen Namen
 * (`demo.bryve.app`). Beides zusammenzukleben ergäbe `.demo.bryve.app`.
 */
export function instanzAdresse(
  instanz: { subdomain?: string | null; domain?: string | null },
): string | null {
  const domain = (instanz.domain ?? '').trim();
  if (!domain) return null;
  const sub = (instanz.subdomain ?? '').trim();
  return `https://${sub ? `${sub}.${domain}` : domain}`;
}
